import axios from "@/config/axios";
import { useToast } from 'vue-toastification';
import Header from "@/components/header/Header.vue";
import Footer from "@/components/footer/Footer.vue";

const toast = useToast();

export default {
  name: 'SettingsLayout',
  components: {
    Header,
    Footer
  },
  data() {
    return {
      accountName: '',
      accountRole: '',
      userAvatar: '',
      isUploadingAvatar: false,
      showDeleteAvatarModal: false,
      isDeletingAvatar: false,
      showOauthAvatarModal: false,
      isCheckingOauthAvatar: false,
      isApplyingOauthAvatar: false,
      oauthAvatar: ''
    };
  },
  computed: {
    activeMenuIcon() {
      return this.$route.path.includes('preferences') 
        ? 'fa-solid fa-wand-magic-sparkles' 
        : 'fa-solid fa-user-gear';
    }
  },
  mounted() {
    this.loadAccountInfo();
    this.userAvatar = localStorage.getItem('userAvatar') || '';
    window.addEventListener('avatar-updated', this.handleAvatarUpdated);
  },
  beforeUnmount() {
    window.removeEventListener('avatar-updated', this.handleAvatarUpdated);
  },
  methods: {
    loadAccountInfo() {
      const currentAccountStr = localStorage.getItem('current-account');
      if (currentAccountStr) {
        try {
          const currentAccount = JSON.parse(currentAccountStr);
          this.accountName = currentAccount.fullName || currentAccount.name || currentAccount.username || '';
          this.accountRole = currentAccount.authority || '';
        } catch (e) {
          console.error('Error parsing current account in layout:', e);
        }
      }
    },
    handleAvatarUpdated(e) {
      this.userAvatar = (e && e.detail) || '';
    },
    triggerAvatarUpload() {
      if (this.isUploadingAvatar || this.isCheckingOauthAvatar) return;
      this.checkOauthAvatar();
    },
    checkOauthAvatar() {
      this.isCheckingOauthAvatar = true;
      axios.post("account/get-oauth-avatar")
        .then(response => {
          if (response.data && response.data.status === '200' && response.data.result) {
            this.oauthAvatar = response.data.result;
            this.showOauthAvatarModal = true;
          } else {
            this.openFilePicker();
          }
        })
        .catch(() => {
          this.openFilePicker();
        })
        .finally(() => {
          this.isCheckingOauthAvatar = false;
        });
    },
    openFilePicker() {
      this.$refs.avatarFileInput?.click();
    },
    pickLocalAvatar() {
      this.showOauthAvatarModal = false;
      this.oauthAvatar = '';
      this.$nextTick(() => {
        this.openFilePicker();
      });
    },
    closeOauthAvatarModal() {
      if (this.isApplyingOauthAvatar) return;
      this.showOauthAvatarModal = false;
      this.oauthAvatar = '';
    },
    applyOauthAvatar() {
      if (this.isApplyingOauthAvatar || !this.oauthAvatar) return;
      this.isApplyingOauthAvatar = true;
      axios.post("account/update-avatar-oauth")
        .then(response => {
          if (response.data && response.data.status === '200' && response.data.result) {
            let avatar = response.data.result;
            if (avatar && !avatar.startsWith('data:') && !avatar.startsWith('http')) {
              avatar = 'data:image/png;base64,' + avatar;
            }
            localStorage.setItem('userAvatar', avatar);
            window.dispatchEvent(new CustomEvent('avatar-updated', { detail: avatar }));
            toast.success(response.data.message || 'Cập nhật ảnh đại diện thành công!');
            this.showOauthAvatarModal = false;
            this.oauthAvatar = '';
          } else {
            toast.error(response.data?.message || 'Cập nhật ảnh đại diện thất bại.');
          }
        })
        .catch(() => {
          toast.error('Có lỗi xảy ra khi cập nhật ảnh đại diện.');
        })
        .finally(() => {
          this.isApplyingOauthAvatar = false;
        });
    },
    onAvatarSelected(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = '';
      if (!file) return;

      if (!file.type || !file.type.startsWith('image/')) {
        toast.error('Vui lòng chọn file ảnh hợp lệ.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ảnh đại diện không được vượt quá 5MB.');
        return;
      }

      this.processAvatarImage(file)
        .then(processed => this.uploadAvatar(processed))
        .catch(() => toast.error('Không thể xử lý ảnh, vui lòng thử ảnh khác.'));
    },
    processAvatarImage(file) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          try {
            const size = 512;
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            const side = Math.min(img.width, img.height);
            const sx = (img.width - side) / 2;
            const sy = (img.height - side) / 2;
            ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
            canvas.toBlob(blob => {
              if (blob) {
                const name = file.name.replace(/\.[^.]+$/, '') || 'avatar';
                resolve(new File([blob], name + '.png', { type: 'image/png' }));
              } else {
                reject(new Error('Canvas toBlob failed'));
              }
            }, 'image/png', 0.95);
          } catch (e) {
            reject(e);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Cannot read image'));
        };
        img.src = url;
      });
    },
    uploadAvatar(file) {
      this.isUploadingAvatar = true;
      const formData = new FormData();
      formData.append('avatar', file);

      axios.post("account/update-avatar", formData)
        .then(response => {
          if (response.data && response.data.status === '200' && response.data.result && response.data.result !== 'NO_AVATAR') {
            let avatar = response.data.result;
            if (avatar && !avatar.startsWith('data:') && !avatar.startsWith('http')) {
              avatar = 'data:image/png;base64,' + avatar;
            }
            if (avatar) {
              localStorage.setItem('userAvatar', avatar);
              window.dispatchEvent(new CustomEvent('avatar-updated', { detail: avatar }));
              toast.success(response.data.message || 'Cập nhật ảnh đại diện thành công!');
            }
          } else {
            toast.error(response.data?.message || 'Cập nhật ảnh đại diện thất bại.');
          }
        })
        .catch(() => {
          toast.error('Có lỗi xảy ra khi cập nhật ảnh đại diện.');
        })
        .finally(() => {
          this.isUploadingAvatar = false;
        });
    },
    deleteAvatar() {
      this.isDeletingAvatar = true;
      axios.post("account/delete-avatar")
        .then(response => {
          if (response.data && response.data.status === '200') {
            localStorage.removeItem('userAvatar');
            window.dispatchEvent(new CustomEvent('avatar-updated', { detail: '' }));
            toast.success(response.data.message || 'Xóa ảnh đại diện thành công!');
          } else {
            toast.error(response.data?.message || 'Xóa ảnh đại diện thất bại.');
          }
        })
        .catch(() => {
          toast.error('Có lỗi xảy ra khi xóa ảnh đại diện.');
        })
        .finally(() => {
          this.isDeletingAvatar = false;
          this.showDeleteAvatarModal = false;
        });
    }
  }
};
